/**
 * Application-wide timezone helper (Feb 2026).
 *
 * The Park City Chiefs organization operates in Eastern Time. All
 * admin-entered dates and times are shown/edited in America/New_York
 * regardless of the admin's browser timezone. Naive ISO strings sent
 * to the backend are interpreted server-side as ET (see backend
 * `tz_utils.py`), so the frontend just needs to render UTC datetimes
 * as ET date/time parts for the form inputs.
 *
 * Handles DST transitions automatically via `Intl.DateTimeFormat`:
 *  * March – November → EDT (UTC-4)
 *  * November – March → EST (UTC-5)
 */

export const APP_TZ = 'America/New_York';

/** Format a UTC ISO date or a Date object as YYYY-MM-DD in Eastern time. */
export function formatDatePartET(input) {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  // Using en-CA gives ISO-shaped output (YYYY-MM-DD) with any tz.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Format a UTC ISO date or Date as HH:MM (24h) in Eastern time. */
export function formatTimePartET(input) {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/**
 * Combine a form-supplied date (YYYY-MM-DD) and time (HH:MM) into a
 * naive ISO string. The backend interprets naive strings as ET and
 * converts to UTC before storing.
 */
export function combineDateTimeForServer(dateStr, timeStr) {
  if (!dateStr || !timeStr) return '';
  return `${dateStr}T${timeStr}:00`;
}

/** Human-readable ET string, e.g. "Jul 21, 2026, 6:00 PM EDT". */
export function formatFriendlyET(input) {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(d);
}
